import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Divider,
  Alert,
  Chip,
  Button,
  TextField,
  Grid,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import TwoFactorSetup from '../components/TwoFactorSetup';
import { Navigate } from 'react-router-dom';

const AdminSettings: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [setupSuccess, setSetupSuccess] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Redirect if not admin
  if (!isAdmin()) {
    return <Navigate to="/" replace />;
  }

  const handleTwoFactorSetupComplete = () => {
    setSetupSuccess(true);
    setShowTwoFactorSetup(false);
    // Optionally refresh user data or show success message
  };

  const handleCloseTwoFactorSetup = () => {
    setShowTwoFactorSetup(false);
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updatePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters long' });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Password updated successfully!' });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setMessage(null);
      }, 5000);
    } catch (error) {
      console.error('Error updating password:', error);
      setMessage({ type: 'error', text: 'Failed to update password' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SettingsIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Admin Settings
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage your administrative account settings and security preferences.
        </Typography>
      </Box>

      {setupSuccess && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          onClose={() => setSetupSuccess(false)}
        >
          Two-Factor Authentication has been successfully configured!
        </Alert>
      )}

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Top Row - Account Information and Security Settings */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Account Information */}
          <Box sx={{ flex: 1 }}>
            <Card elevation={2}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <ShieldIcon sx={{ fontSize: 24, mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Account Information
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Email Address
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {user?.email}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Role
                  </Typography>
                  <Chip 
                    label={user?.role?.toUpperCase()} 
                    color="primary" 
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    User ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                    {user?.id || 'Not available'}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Two-Factor Authentication Status
                  </Typography>
                  <Chip 
                    label={user?.two_factor_enabled ? 'ENABLED' : 'DISABLED'}
                    color={user?.two_factor_enabled ? 'success' : 'warning'}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Security Settings */}
          <Box sx={{ flex: 1 }}>
            <Card elevation={2}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <SecurityIcon sx={{ fontSize: 24, mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Security Settings
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Enhance your account security with two-factor authentication. This adds an extra layer of protection to your admin account.
                </Typography>
                
                {!user?.two_factor_enabled ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setShowTwoFactorSetup(true)}
                    startIcon={<SecurityIcon />}
                  >
                    Set Up Two-Factor Authentication
                  </Button>
                ) : (
                  <Alert severity="success">
                    Two-Factor Authentication is already enabled for your account.
                  </Alert>
                )}
                
                <TwoFactorSetup 
                  open={showTwoFactorSetup}
                  onClose={handleCloseTwoFactorSetup}
                  onSetupComplete={handleTwoFactorSetupComplete}
                />
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Change Password Section */}
        <Card elevation={2}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <LockIcon sx={{ fontSize: 24, mr: 2, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Change Password
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Update your account password to maintain security. Make sure to use a strong password.
            </Typography>
            
            <form autoComplete="off" data-form-type="other">
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  type="password"
                  label="Current Password"
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                  disabled={loading}
                  inputProps={{
                    autoComplete: 'current-password',
                    'data-form-type': 'other'
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  type="password"
                  label="New Password"
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  disabled={loading}
                  helperText="Minimum 6 characters"
                  inputProps={{
                    autoComplete: 'new-password',
                    'data-form-type': 'other'
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm New Password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                  disabled={loading}
                  inputProps={{
                    autoComplete: 'new-password',
                    'data-form-type': 'other'
                  }}
                />
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={updatePassword}
                disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                startIcon={<LockIcon />}
              >
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </Box>
            </form>
          </CardContent>
        </Card>

        {/* Security Guidelines */}
        <Card elevation={2}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Security Best Practices
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1, p: 2, bgcolor: '#eff6ff', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  🔐 Strong Passwords
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use complex passwords with a mix of letters, numbers, and symbols.
                </Typography>
              </Box>
              
              <Box sx={{ flex: 1, p: 2, bgcolor: '#f0fdf4', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  📱 Two-Factor Authentication
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Enable 2FA to add an extra layer of security to your account.
                </Typography>
              </Box>
              
              <Box sx={{ flex: 1, p: 2, bgcolor: '#fffbeb', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  🚪 Secure Logout
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Always log out when finished, especially on shared computers.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default AdminSettings;